// `defineEvent` (plan §4.3): a versioned fact. Any payload change needs a new `version`, and every version below the
// current one needs its step in `upcastFrom` (define/upcast.ts), so a fact stored at any version reaches the current
// shape. Lint and the breaking-change detector in compat/ require the whole chain. Payload schemas describe the wire,
// so each step maps one version's wire payload to the next one's.
import type { z } from 'zod';
import type { AggregateKind, MergeClass } from './meta.ts';
import type { Downcaster, Upcaster } from './upcast.ts';

export type { Upcaster } from './upcast.ts';

export interface EventDef<N extends string = string, P extends z.ZodType = z.ZodType> {
  readonly kind: 'event';
  /** PascalCase fact, e.g. 'OrderVoided'. */
  readonly name: N;
  readonly version: number;
  readonly summary: string;
  readonly aggregate: AggregateKind;
  readonly payload: P;
  /** Payload paths sync re-verification compares (money, lifecycle, references). */
  readonly verifiable: readonly string[];
  readonly merge: MergeClass;
  /** Pulled by devices, so it is bound by the supported app-version window. */
  readonly deviceConsumed: boolean;
  /** One step per older version, 1 through `version - 1` (C8). */
  readonly upcastFrom?: readonly Upcaster[];
  /**
   * Steps down to older versions a register may still run (`version` n reads n + 1). Where they reach a puller's
   * declared maximum the receiver sends the fact downcast; otherwise it holds the feed and answers `update_required`.
   */
  readonly downcastTo?: readonly Downcaster[];
  readonly pii?: readonly string[];
}

// The shape every catalog list accepts.
export type AnyEventDef = EventDef;

export function defineEvent<const N extends string, P extends z.ZodType>(def: Omit<EventDef<N, P>, 'kind'>): EventDef<N, P> {
  return { ...def, kind: 'event' };
}
